/**
 * Bloques de la columna izquierda de la lámina.
 *
 * Cada bloque expone una función de altura y una de dibujo. La separación no es
 * cosmética: la columna se compone midiendo TODO antes de dibujar nada, para
 * repartir el espacio sobrante como aire entre bloques en vez de dejarlo al pie.
 *
 * Todos reciben el mismo `sheet`, un objeto plano con el estado de la lámina:
 *   { deas, buildings, coveragePct, radius, areaM2, meta, aedImage, date }
 */

import {
  clientDeaCount,
  complianceGaps,
  hasExternalDea,
  horizontalCompliant,
  isCompliant,
  isPublicLevel,
  isSharedLevel,
  levelsBottomUp,
  levelsToInstall,
  missingDeaCount,
  sharedDeaNumber,
  verticalApplies,
  verticalCompliant,
  verticalCorrectiveAction,
} from '../domain/compliance.js';
import { totalLevels } from '../domain/levels.js';
import { equipmentLabel, equipmentNumbers } from '../domain/numbering.js';
import {
  isInstalledMode,
  missingEquipment,
  requiredEquipment,
  sharedTotal,
  totalEquipment,
  verticalEquipment,
} from '../domain/inventory.js';
import { formatArea, monthYear } from '../domain/document.js';
import { clipText, countLines, drawPanel, drawVerdictPill, wrapText } from './canvas-utils.js';

const PANEL_FILL = 'rgba(20,32,43,.6)';
const PANEL_STROKE = 'rgba(255,255,255,.65)';
const TITLE_COLOR = '#9fd3b3';

const areaText = (sheet) => (sheet.areaM2 > 0 ? formatArea(sheet.areaM2) : '—');

// ---------------------------------------------------------------- Bloque 1: total

export function totalHeight(sheet) {
  return isInstalledMode(sheet.deas) ? 122 : 116;
}

export function drawTotal(ctx, x, y, width, sheet) {
  const proposal = !isInstalledMode(sheet.deas);
  const total = totalEquipment(sheet.deas, sheet.buildings);
  const vertical = verticalEquipment(sheet.deas, sheet.buildings);
  const height = totalHeight(sheet);
  const { aedImage } = sheet;
  const hasIcon = aedImage?.complete && aedImage.naturalWidth;

  drawPanel(ctx, x, y, width, height, { fill: PANEL_FILL, stroke: PANEL_STROKE });
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (proposal) {
    // Propuesta: un número único, la dotación estimada.
    ctx.fillStyle = TITLE_COLOR;
    ctx.font = 'bold 13px Arial';
    ctx.fillText('TOTAL EQUIPOS DEA ESTIMADOS', x + width / 2, y + 20);
    ctx.font = 'bold 40px Arial';
    const numWidth = ctx.measureText(String(total)).width;
    const groupX = x + width / 2 - (30 + 10 + numWidth) / 2;
    if (hasIcon) ctx.drawImage(aedImage, groupX, y + 34, 30, 40);
    ctx.textAlign = 'left';
    ctx.fillStyle = '#fff';
    ctx.fillText(String(total), groupX + 40, y + 56);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#9fb0c2';
    ctx.font = '10.5px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(
      `${sheet.deas.length} cobertura horizontal${vertical > 0 ? `  +  ${vertical} en altura` : ''}`,
      x + width / 2,
      y + 100,
    );
    return y + height;
  }

  // Instalado: instalados contra requeridos, y la brecha explícita.
  const required = requiredEquipment(sheet.deas, sheet.buildings, sheet.coveragePct);
  const missing = missingEquipment(sheet.coveragePct, sheet.buildings);
  ctx.fillStyle = TITLE_COLOR;
  ctx.font = 'bold 13px Arial';
  ctx.fillText('EQUIPOS DEA · ESTADO GENERAL', x + width / 2, y + 18);

  const leftX = x + width * 0.27;
  const rightX = x + width * 0.73;
  ctx.strokeStyle = 'rgba(255,255,255,.18)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x + width / 2, y + 36);
  ctx.lineTo(x + width / 2, y + 82);
  ctx.stroke();

  ctx.textBaseline = 'alphabetic';
  ctx.font = 'bold 8.5px Arial';
  ctx.fillStyle = '#6f8496';
  ctx.fillText('INSTALADOS', leftX, y + 44);
  ctx.fillText('REQUERIDOS', rightX, y + 44);

  ctx.font = 'bold 34px Arial';
  const installedText = String(total);
  const installedWidth = ctx.measureText(installedText).width;
  const iconWidth = 22;
  const gap = 6;
  const groupWidth = hasIcon ? iconWidth + gap + installedWidth : installedWidth;
  const groupX = leftX - groupWidth / 2;
  if (hasIcon) ctx.drawImage(aedImage, groupX, y + 50, iconWidth, 29);
  ctx.textAlign = 'left';
  ctx.fillStyle = '#fff';
  ctx.fillText(installedText, groupX + (hasIcon ? iconWidth + gap : 0), y + 76);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 34px Arial';
  ctx.fillText(String(required), rightX, y + 76);

  const stripY = y + 90;
  const stripHeight = 24;
  ctx.fillStyle = missing > 0 ? 'rgba(209,58,94,.22)' : 'rgba(47,158,79,.20)';
  ctx.beginPath();
  ctx.roundRect(x + 14, stripY, width - 28, stripHeight, 8);
  ctx.fill();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 11px Arial';
  ctx.fillStyle = missing > 0 ? '#f4a8bd' : '#9fe1cb';
  const stripText =
    missing > 0
      ? missing === 1
        ? 'Falta 1 punto cardioprotegido'
        : `Faltan ${missing} puntos cardioprotegidos`
      : 'Dotación completa según norma';
  ctx.fillText(stripText, x + width / 2, stripY + stripHeight / 2 + 1);
  return y + height;
}

// -------------------------------------------------- Bloque 2: cumplimiento normativo

export function complianceHeight(sheet, width) {
  if (!isInstalledMode(sheet.deas)) return 82;
  let height = 44 + 40 + 40; // cabecera + fila horizontal + estado general
  if (sheet.buildings.length) height += 40; // fila vertical
  const gaps = complianceGaps(sheet.coveragePct, sheet.buildings, {
    canRelocateHorizontal: sheet.canRelocateHorizontal,
  });
  if (gaps.length) {
    let lines = 0;
    gaps.forEach((gap) => {
      lines += countLines(`•  ${gap}`, '10px Arial', width - 44);
    });
    height += 24 + lines * 13 + 8;
  } else {
    height += 6;
  }
  height += 15; // leyenda "inc. = inciso del artículo"
  return height;
}

export function drawCompliance(ctx, x, y, width, sheet) {
  const proposal = !isInstalledMode(sheet.deas);
  const height = complianceHeight(sheet, width);

  drawPanel(ctx, x, y, width, height, {
    fill: PANEL_FILL,
    stroke: proposal ? 'rgba(255,233,168,.5)' : PANEL_STROKE,
  });
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (proposal) {
    // Una propuesta no se evalúa contra la norma: todavía no hay nada instalado
    // que pueda cumplir o incumplir. Decir "NO CUMPLE" acá sería falso.
    ctx.fillStyle = '#ffe9a8';
    ctx.font = 'bold 13px Arial';
    ctx.fillText('PROPUESTA DE CARDIOPROTECCIÓN', x + width / 2, y + 22);
    ctx.fillStyle = '#7f94a6';
    ctx.font = 'italic 9px Arial';
    ctx.fillText('Dimensionamiento según Ley 21.156 · D.S. 56/2020', x + width / 2, y + 48);
    ctx.fillText('Propuesta — sin evaluación de cumplimiento', x + width / 2, y + 64);
    return y + height;
  }

  ctx.fillStyle = TITLE_COLOR;
  ctx.font = 'bold 13px Arial';
  ctx.fillText('CUMPLIMIENTO NORMATIVO', x + width / 2, y + 20);
  ctx.fillStyle = '#7f94a6';
  ctx.font = 'italic 9px Arial';
  ctx.fillText('Ley 21.156 · Reglamento D.S. 56/2020, Art. 5º', x + width / 2, y + 34);

  let rowY = y + 44;
  const row = (label, sublabel, ok) => {
    ctx.fillStyle = ok ? 'rgba(60,180,75,.10)' : 'rgba(230,25,75,.10)';
    ctx.strokeStyle = ok ? 'rgba(120,230,150,.55)' : 'rgba(240,120,150,.55)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x + 12, rowY, width - 24, 36, 7);
    ctx.fill();
    ctx.stroke();
    const pillX = drawVerdictPill(
      ctx,
      x + width - 12,
      rowY + 18,
      ok,
      ok ? 'CUMPLE' : 'NO CUMPLE',
      10.5,
    );
    const maxWidth = pillX - 8 - (x + 22);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#dfe6ea';
    ctx.font = 'bold 10.5px Arial';
    ctx.fillText(clipText(ctx, label, maxWidth), x + 22, rowY + 15);
    ctx.fillStyle = '#8296a6';
    ctx.font = '8.5px Arial';
    ctx.fillText(clipText(ctx, sublabel, maxWidth), x + 22, rowY + 28);
    rowY += 40;
  };

  row(
    'Cobertura horizontal',
    `todo punto a ${sheet.radius} m o menos de un DEA · inc. 1`,
    horizontalCompliant(sheet.coveragePct),
  );
  if (sheet.buildings.length) {
    // El subtítulo tiene que decir la regla REAL. Decía "1 DEA cada 3 pisos",
    // que es la regla vieja y contradice el veredicto que la propia caja emite.
    row(
      'Cobertura vertical',
      '1 DEA cubre su nivel y 2 arriba y abajo · inc. 2',
      verticalCompliant(sheet.buildings),
    );
  }

  const compliant = isCompliant(sheet.coveragePct, sheet.buildings);
  ctx.fillStyle = compliant ? 'rgba(60,180,75,.2)' : 'rgba(230,25,75,.2)';
  ctx.strokeStyle = compliant ? '#78e696' : '#f07896';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(x + 12, rowY, width - 24, 34, 8);
  ctx.fill();
  ctx.stroke();
  const generalPillX = drawVerdictPill(
    ctx,
    x + width - 14,
    rowY + 17,
    compliant,
    compliant ? 'CUMPLE' : 'NO CUMPLE',
    10.5,
  );
  const generalMax = generalPillX - 8 - (x + 22);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#cfe0ee';
  ctx.font = 'bold 10.5px Arial';
  ctx.fillText(clipText(ctx, 'CUMPLIMIENTO ART. 5º', generalMax), x + 22, rowY + 14);
  ctx.fillStyle = '#8296a6';
  ctx.font = '8.5px Arial';
  ctx.fillText(clipText(ctx, 'Ley 21.156 · D.S. 56/2020', generalMax), x + 22, rowY + 26);
  rowY += 40;

  const gaps = complianceGaps(sheet.coveragePct, sheet.buildings, {
    canRelocateHorizontal: sheet.canRelocateHorizontal,
  });
  if (gaps.length) {
    const boxHeight = y + height - 8 - 15 - rowY;
    ctx.fillStyle = 'rgba(230,25,75,.08)';
    ctx.strokeStyle = 'rgba(240,120,150,.4)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x + 12, rowY, width - 24, boxHeight, 7);
    ctx.fill();
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#ff9d9d';
    ctx.font = 'bold 10.5px Arial';
    ctx.fillText('⚠  BRECHAS DE ACCIÓN', x + 22, rowY + 15);
    ctx.fillStyle = '#dfe6ea';
    ctx.font = '10px Arial';
    let gapY = rowY + 30;
    gaps.forEach((gap) => {
      gapY = wrapText(ctx, `•  ${gap}`, x + 22, gapY, width - 44, 13);
    });
  }

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = 'italic 8px Arial';
  ctx.fillStyle = '#7f94a6';
  ctx.fillText('inc. = inciso del artículo', x + 12, y + height - 6);
  return y + height;
}

// --------------------------------------------- Bloque 3: detalle cobertura horizontal

function horizontalRows(sheet) {
  return [
    ['DEA cobertura horizontal', `${sheet.deas.length} DEA`],
    ['Radio por DEA', `${sheet.radius} m`],
    ['Superficie', areaText(sheet)],
  ];
}

export function horizontalHeight(sheet) {
  const band = sheet.coveragePct != null ? 58 : 0;
  return 30 + band + horizontalRows(sheet).length * 24 + 12;
}

export function drawHorizontal(ctx, x, y, width, sheet) {
  const rows = horizontalRows(sheet);
  const height = horizontalHeight(sheet);

  drawPanel(ctx, x, y, width, height, { fill: PANEL_FILL, stroke: PANEL_STROKE });
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = TITLE_COLOR;
  ctx.font = 'bold 13px Arial';
  ctx.fillText('COBERTURA HORIZONTAL', x + width / 2, y + 18);

  let rowY = y + 30;
  if (sheet.coveragePct != null) {
    ctx.fillStyle = 'rgba(60,180,75,.16)';
    ctx.strokeStyle = 'rgba(120,230,150,.75)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.roundRect(x + 10, rowY, width - 20, 46, 9);
    ctx.fill();
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#bfe6c8';
    ctx.font = 'bold 10.5px Arial';
    ctx.fillText('SUPERFICIE ALCANZADA', x + 22, rowY + 16);
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#8296a6';
    ctx.font = 'italic 9px Arial';
    ctx.fillText(`radio ${sheet.radius} m de cada DEA · D.S. 56/2020`, x + 22, rowY + 38);
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#8bf0a8';
    ctx.font = 'bold 28px Arial';
    ctx.fillText(`${sheet.coveragePct}%`, x + width - 18, rowY + 24);
    rowY += 58;
  }

  rows.forEach(([label, value]) => {
    ctx.strokeStyle = 'rgba(255,255,255,.1)';
    ctx.beginPath();
    ctx.moveTo(x + 10, rowY);
    ctx.lineTo(x + width - 10, rowY);
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#9fb0c2';
    ctx.font = '10.5px Arial';
    ctx.fillText(label, x + 12, rowY + 12);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 10.5px Arial';
    ctx.fillText(value, x + width - 12, rowY + 12);
    rowY += 24;
  });
  return y + height;
}

// ------------------------------------- Bloque 4: elevación por edificio (vertical)

const ELEV_HEAD = 98;
const ELEV_ROW = 22;
const ELEV_PAD = 12;
const ELEV_FOOT = 46;

export function elevationHeight(building) {
  return ELEV_HEAD + totalLevels(building) * ELEV_ROW + ELEV_FOOT;
}

export function drawElevation(ctx, x, y, width, building, numbering, buildingIndex) {
  const missing = missingDeaCount(building);
  const compliant = missing === 0;
  const height = elevationHeight(building);
  const levels = levelsBottomUp(building).slice().reverse();

  drawPanel(ctx, x, y, width, height, { fill: PANEL_FILL, stroke: PANEL_STROKE });

  // — Cabecera —
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = TITLE_COLOR;
  ctx.font = 'bold 13px Arial';
  ctx.fillText('COBERTURA VERTICAL', x + width / 2, y + 18);
  ctx.strokeStyle = 'rgba(255,255,255,.14)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 16, y + 31);
  ctx.lineTo(x + width - 16, y + 31);
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 11.5px Arial';
  ctx.fillText(building.nombre, x + width / 2, y + 45);

  // Subtítulo con la composición explícita: el total es lo que manda la regla.
  const sub = building.sub || 0;
  const composition = sub
    ? `${building.pisos} pisos + ${sub} subt. = ${totalLevels(building)} niveles`
    : `${totalLevels(building)} niveles`;
  ctx.fillStyle = '#8296a6';
  ctx.font = '9px Arial';
  ctx.fillText(composition, x + width / 2, y + 58);

  const missingLabel = missing === 1 ? 'FALTA 1 PCP' : `FALTAN ${missing} PCP`;
  drawVerdictPill(
    ctx,
    x + width / 2,
    y + 76,
    compliant,
    compliant ? 'CUMPLE' : `NO CUMPLE · ${missingLabel}`,
    11,
    true,
  );

  ctx.textBaseline = 'alphabetic';
  ctx.font = 'bold 8.5px Arial';
  ctx.fillStyle = '#6f8496';
  ctx.textAlign = 'left';
  ctx.fillText('NIVEL', x + 14, y + 92);
  ctx.fillText('ESTADO ACTUAL', x + 48, y + 92);

  // — Filas de niveles, de arriba hacia abajo como se lee un corte —
  const barX = x + 48;
  const barWidth = width - 48 - ELEV_PAD;
  let rowY = y + ELEV_HEAD;

  levels.forEach((level) => {
    ctx.fillStyle = level < 0 ? '#6f8496' : '#8fa6ba';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(`N${level}`, barX - 6, rowY + ELEV_ROW / 2);

    // Sólo el estado REAL. Lo que hay que hacer va en las brechas, no acá.
    let fill = '#c9c3b6';
    let stroke = '#8a8577';
    let text = '';
    let textColor = '#fff';

    if (clientDeaCount(building, level) > 0) {
      fill = '#3cb44b';
      stroke = '#1f6b2a';
      const numbers = numbering ? equipmentNumbers(numbering, buildingIndex, level) : [];
      const names = (building.pisoNombres || {})[level];
      text = numbers.length ? equipmentLabel(numbers, names) : 'DEA instalado';
      if (isSharedLevel(building, level)) {
        const shared = sharedDeaNumber(building, level);
        text += shared ? ` · comparte D${shared}` : ' · equipo de terreno';
      }
    } else if (hasExternalDea(building, level)) {
      fill = '#d9a441';
      stroke = '#8a6410';
      text = 'DEA externo';
    } else if (!isPublicLevel(building, level)) {
      fill = '#4a5560';
      stroke = '#2c343c';
      text = 'sin uso público';
      textColor = '#b9c6d2';
    }

    ctx.fillStyle = fill;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(barX, rowY + 3, barWidth, ELEV_ROW - 6, 3);
    ctx.fill();
    ctx.stroke();

    if (text) {
      ctx.fillStyle = textColor;
      ctx.font = 'bold 9px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(clipText(ctx, text, barWidth - 8), barX + barWidth / 2, rowY + ELEV_ROW / 2 + 0.5);
    }
    rowY += ELEV_ROW;
  });

  // — Acción correctiva al pie: qué hacer, en la unidad del cliente —
  const footY = rowY + 8;
  ctx.strokeStyle = 'rgba(255,255,255,.14)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 16, footY);
  ctx.lineTo(x + width - 16, footY);
  ctx.stroke();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  const action = verticalCorrectiveAction(building);
  if (action) {
    ctx.fillStyle = '#ff9d9d';
    ctx.font = 'bold 9px Arial';
    wrapText(ctx, `Acción: ${action}`, x + 14, footY + 15, width - 28, 11);
  }
  ctx.font = 'italic 8px Arial';
  ctx.fillStyle = '#8296a6';
  ctx.fillText('PCP = DEA + gabinete + señalética', x + 14, footY + ELEV_FOOT - 8);

  return { x, y, w: width, h: height };
}

// ---------------------------------------------------------------- Bloque 5: leyenda

export function legendItems(sheet) {
  // El color identifica cada DEA; el estado lo marca sólida vs punteada. Van
  // desacoplados a propósito: si el color codificara el estado, no se podría
  // distinguir un DEA de otro en un plano con varios círculos superpuestos.
  const items = [
    { type: 'circle', color: '#cfe0ee', dash: false, label: 'Cobertura adquirida (línea sólida)' },
    { type: 'circle', color: '#cfe0ee', dash: true, label: 'Cobertura propuesta (punteada)' },
    { type: 'line', color: '#ffdd00', label: 'Perímetro del recinto' },
  ];
  if (sheet.deas.length > 1) {
    items.push({ type: 'note', label: 'Cada color identifica un DEA distinto' });
  }
  items.push({
    type: 'text',
    label: `D1, D2… = n.º de DEA${sheet.buildings.length ? '  ·  E1, E2… = n.º de edificio' : ''}`,
  });
  if (sheet.buildings.length) {
    items.push({ type: 'building', label: 'Edificio' });
    items.push({ type: 'square', color: '#3cb44b', label: 'Nivel con DEA del cliente' });
    // Sólo se explica lo que de verdad aparece en esta lámina.
    if (sheet.buildings.some((b) => (b.ext || []).length)) {
      items.push({ type: 'square', color: '#d9a441', label: 'Nivel con DEA externo (de tercero)' });
    }
    if (sheet.buildings.some((b) => (b.noPub || []).length)) {
      items.push({ type: 'square', color: '#4a5560', label: 'Nivel sin uso público' });
    }
    items.push({ type: 'text', label: 'N1, N2… pisos  ·  N-1, N-2… subterráneos' });
    if (sharedTotal(sheet.buildings) > 0) {
      items.push({ type: 'text', label: 'Nivel con "comparte Dn" = mismo equipo de terreno' });
    }
    items.push({
      type: 'text',
      label: '1 DEA cubre su nivel y 2 arriba y abajo · externo: sólo el suyo',
    });
  }
  items.push({ type: 'text', label: 'Punto cardioprotegido = DEA + gabinete + señalética' });
  return items;
}

export function legendHeight(sheet) {
  return 30 + legendItems(sheet).length * 24 + 12;
}

export function drawLegend(ctx, x, y, width, sheet) {
  const items = legendItems(sheet);
  const height = legendHeight(sheet);

  drawPanel(ctx, x, y, width, height, { fill: PANEL_FILL, stroke: PANEL_STROKE });
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = TITLE_COLOR;
  ctx.font = 'bold 13px Arial';
  ctx.fillText('LEYENDA', x + width / 2, y + 18);

  let rowY = y + 40;
  const symbolX = x + 22;

  items.forEach((item) => {
    if (item.type === 'circle') {
      ctx.beginPath();
      ctx.arc(symbolX, rowY, 8, 0, 2 * Math.PI);
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = item.color;
      ctx.setLineDash(item.dash ? [4, 3] : []);
      ctx.stroke();
      ctx.setLineDash([]);
    } else if (item.type === 'line') {
      ctx.strokeStyle = item.color;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(symbolX - 8, rowY);
      ctx.lineTo(symbolX + 8, rowY);
      ctx.stroke();
    } else if (item.type === 'note') {
      ['#4a78d0', '#3cb44b', '#e6194b'].forEach((color, index) => {
        ctx.beginPath();
        ctx.arc(symbolX - 7 + index * 7, rowY, 3.2, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
      });
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#8296a6';
      ctx.font = 'italic 9px Arial';
      ctx.fillText(item.label, x + 40, rowY);
      rowY += 24;
      return;
    } else if (item.type === 'text') {
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#8296a6';
      ctx.font = 'italic 9px Arial';
      ctx.fillText(item.label, x + 22, rowY);
      rowY += 24;
      return;
    } else if (item.type === 'building') {
      const bw = 12;
      const bh = 16;
      const bx = symbolX - bw / 2;
      const by = rowY - bh / 2;
      ctx.fillStyle = 'rgba(16,26,36,.55)';
      ctx.fillRect(bx, by, bw, bh);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(bx, by, bw, bh);
      ctx.lineWidth = 1;
      [5, 10].forEach((oy) => {
        ctx.beginPath();
        ctx.moveTo(bx, by + oy);
        ctx.lineTo(bx + bw, by + oy);
        ctx.stroke();
      });
      ctx.beginPath();
      ctx.moveTo(bx + bw / 2, by);
      ctx.lineTo(bx + bw / 2, by + bh);
      ctx.stroke();
    } else {
      ctx.fillStyle = item.color;
      ctx.fillRect(symbolX - 8, rowY - 6, 16, 12);
      ctx.strokeStyle = 'rgba(255,255,255,.5)';
      ctx.lineWidth = 1;
      ctx.strokeRect(symbolX - 8, rowY - 6, 16, 12);
    }

    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#dfe6ea';
    ctx.font = '10px Arial';
    ctx.fillText(item.label, x + 40, rowY);
    rowY += 24;
  });

  return y + height;
}

// ---------------------------------------------------------------- Bloque 6: cajetín

export function titleBlockRows(sheet) {
  const { meta } = sheet;
  const rows = [['Cliente', meta.sitio || '—']];
  if (meta.sede) rows.push(['Sede', meta.sede]);
  rows.push(
    ['Dirección', meta.direccion || '—'],
    ['Fecha', monthYear(sheet.date)],
    ['N° Lámina', meta.numLamina],
    ['Código doc.', meta.codigoDoc],
    ['Superficie', areaText(sheet)],
    ['Normativa', 'Ley 21.156 · Art. 5º'],
    ['Elaboró', 'VitalHeart'],
  );
  return rows;
}

export function titleBlockHeight(sheet) {
  return 30 + titleBlockRows(sheet).length * 22 + 24;
}

export function drawTitleBlock(ctx, x, y, width, sheet) {
  const rows = titleBlockRows(sheet);
  const height = titleBlockHeight(sheet);

  drawPanel(ctx, x, y, width, height, { fill: 'rgba(15,26,35,.85)', stroke: PANEL_STROKE });
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = TITLE_COLOR;
  ctx.font = 'bold 13px Arial';
  ctx.fillText('DATOS DEL PROYECTO', x + width / 2, y + 18);

  let rowY = y + 34;
  rows.forEach(([label, value]) => {
    ctx.strokeStyle = 'rgba(255,255,255,.08)';
    ctx.beginPath();
    ctx.moveTo(x + 10, rowY);
    ctx.lineTo(x + width - 10, rowY);
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#8fa6ba';
    ctx.font = '10.5px Arial';
    ctx.fillText(label, x + 12, rowY + 11);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 10.5px Arial';
    ctx.fillText(clipText(ctx, String(value), width - 90), x + width - 12, rowY + 11);
    rowY += 22;
  });

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#5f7386';
  ctx.font = '9px Arial';
  ctx.fillText('SOMOS VITALGROUP · vitalg.cl', x + width / 2, y + height - 12);
  return y + height;
}
